import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Share2, Trash2, Briefcase, Clock, MapPin } from 'lucide-react';
import { workers } from '../data/workers';
import { useFavorites } from '../hooks/useFavorites';
import { useState } from 'react';

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { favorites, isFavorited, toggleFavorite, generateShareLink } =
    useFavorites(workers);
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    const link = generateShareLink();
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRemoveAll = () => {
    favorites.forEach((w) => toggleFavorite(w.id));
  };

  return (
    <div className="px-4 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-text-primary">Favorites</h2>
          <p className="text-xs text-text-muted mt-0.5">
            {favorites.length} worker{favorites.length !== 1 ? 's' : ''} saved
          </p>
        </div>
        {favorites.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-2 bg-surface-alt text-primary text-xs font-medium rounded-xl hover:bg-primary hover:text-white transition-all"
            >
              <Share2 className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Share'}
            </button>
            <button
              onClick={handleRemoveAll}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-danger text-xs font-medium rounded-xl hover:bg-red-100 transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Shared link hint */}
      {copied && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-success/10 text-success text-xs font-medium px-3 py-2 rounded-lg mb-3"
        >
          Shortlist link copied to clipboard!
        </motion.div>
      )}

      {/* Favorites list */}
      {favorites.length > 0 ? (
        <div className="space-y-3">
          {favorites.map((worker, i) => (
            <motion.div
              key={worker.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl shadow-card overflow-hidden"
            >
              <div className="flex">
                {/* Thumbnail */}
                <div
                  className="w-28 h-28 bg-gray-900 video-placeholder flex-shrink-0 cursor-pointer"
                  onClick={() => navigate(`/worker/${worker.id}`)}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary-dark/90 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="8,5 19,12 8,19" />
                    </svg>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-text-primary">
                        {worker.firstName}
                      </h3>
                      <p className="text-xs text-text-secondary truncate">
                        {worker.specialty}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleFavorite(worker.id)}
                      className="p-1 -mr-1 flex-shrink-0"
                    >
                      <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-text-muted mt-1">
                    <span className="flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      {worker.yearsOfExperience}y exp
                    </span>
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />
                      {worker.nationality}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/worker/${worker.id}`)}
                    className="mt-2 text-primary text-xs font-medium flex items-center gap-1"
                  >
                    View Profile →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-20 text-text-muted">
          <Heart className="w-12 h-12 mb-3 opacity-20" />
          <p className="text-sm font-medium">No favorites yet</p>
          <p className="text-xs mt-1 mb-4">
            Tap the heart on any worker to save them here
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary-light transition-colors"
          >
            Browse Workers
          </button>
        </div>
      )}
    </div>
  );
}